import React from 'react';
import axios from 'axios';
import classNames from 'classnames';
import { Button, Card, Copy, Icons, Image, Title } from '#components/atoms';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '#components/atoms/carousel-updated';
import { BackgroundGradientAnimation } from '#components/atoms/background-gradient';
import { dateInRangeCompare } from 'src/libs/date-compare';
import { useWindowSize } from '../../hooks/use-window-size';

export const HeroNews = () => {
  const [data, setData] = React.useState<any>([]);
  const { title, header, cards, description } = data;
  const SIZE = useWindowSize();

  React.useEffect(() => {
    const fetch = async () => {
      const response = (await axios.get('/news.json')) as any;
      setData(response.data);
    };

    fetch();
  }, []);

  const FILTER_BANNERS = cards?.filter((item: any) =>
    dateInRangeCompare({
      dateStart: item.date_start,
      dateEnd: item.date_end,
    }),
  );
  const BANNERS =
    FILTER_BANNERS?.length <= 3 && FILTER_BANNERS?.length > 1 && SIZE < 1830
      ? [...FILTER_BANNERS, ...FILTER_BANNERS]
      : FILTER_BANNERS;

  return (
    <div id="hero-news">
      <BackgroundGradientAnimation containerClassName="!h-[880px] md:!h-[540px] lg:!h-[720px] !overflow-hidden" />
      <Carousel
        className="relative overflow-hidden"
        opts={{
          align: 'start',
          loop: true,
        }}
      >
        <div className="max-w-screen-2xl mx-auto px-5 lg:pl-24 py-24 lg:!py-40 lg:!pb-40 !pb-8 gap-10 flex flex-col md:grid md:grid-cols-12 overflow-hidden lg:bg-center bg-no-repeat">
          <div className="z-40 col-span-6 px-5 flex flex-col justify-between md:h-[360px] lg:h-[466px]">
            <div className="w-full">
              <Copy className="!text-base mb-1 md:!text-lg lg:!text-xl 2xl:!text-2xl !font-bold !text-blue-400">
                {header}
              </Copy>
              <Title className="z-10 !text-4xl md:font-medium lg:!text-[60px] 2xl:!text-6xl 2xl:!leading-[90px] lg:!leading-[64px] !text-slate-900">
                {title}
              </Title>
              <Copy className="z-10 mt-3 md:mt-5 mb-5 md:mb-0 !font-normal !text-xl lg:!text-3xl md:block 2xl:!text-2xl !text-slate-500">
                {description}
              </Copy>
            </div>
            <div className="md:flex gap-7 hidden">
              <CarouselPrevious className="!static transform-none !p-0 !rounded-full !size-12 bg-slate-200 !text-slate-500" />
              <CarouselNext className="!static transform-none !p-0 !rounded-full  !size-12 bg-slate-200 !text-slate-500" />
            </div>
          </div>
        </div>
        <SuccessStoriesCarousel data={BANNERS} />
        <div className="flex gap-7 md:hidden pl-5 mt-5 z-10">
          <CarouselPrevious className="!static transform-none !p-0 !rounded-full !size-12 bg-slate-200 !text-slate-500" />
          <CarouselNext className="!static transform-none !p-0 !rounded-full  !size-12 bg-slate-200 !text-slate-500" />
        </div>
      </Carousel>
    </div>
  );
};

const SuccessStoriesCarousel = ({ data }: { data: any }) => (
  <div className="w-full md:col-span-6 !h-[378px] lg:h-[517px] pl-5 md:absolute top-20 lg:top-28 md:translate-x-[50%]">
    <div className="w-full md:max-w-[700px] 2xl:max-w-[1000px] xl2:max-w-full">
      <CarouselContent>
        {data?.map((item: any, index: number) => (
          <CarouselItem key={index} className="!basis-auto lg:ml-4 2xl:ml-0">
            <Card
              className={classNames(
                'flex-col !h-[378px] !justify-start !pt-5 lg:!pt-10 !px-0 !pb-0 !w-[286px] lg:!h-[517px] !shadow-md lg:!w-[432px] !rounded-2xl mb-4 overflow-hidden border',
                item.background,
              )}
            >
              <div className="w-full h-[100px] lg:h-36 flex items-center">
                <Image
                  src={`/news/${item.image}`}
                  alt="device-frame"
                  className="h-full lg:h-full w-full"
                />
              </div>
              {item.header && (
                <span
                  className={classNames(
                    'text-base text-center font-semibold text-slate-50 lg:text-2xl px-5 lg:px-10',
                  )}
                >
                  {item.header}
                </span>
              )}
              <span
                className={classNames(
                  'text-base text-center font-semibold lg:text-2xl px-5 lg:px-10',
                  item.text_color,
                )}
              >
                {item.title}
              </span>
              {item.subtitle ? (
                <div className="px-7 lg:px-10 w-full">
                  <div className="w-full opacity-90 bg-slate-50 shadow-md text-gray-800 rounded-lg p-3 lg:p-4 flex flex-col items-center">
                    <div className="flex flex-col items-center justify-between w-full">
                      <div className="flex items-center justify-between w-full">
                        <Icons
                          className={classNames(
                            item.color_icons,
                            'size-7 rotate-12',
                          )}
                          fill="#FFFFFF"
                          type={item.icon}
                        />

                        <div className="text-center">
                          <h3
                            className={classNames(
                              'text-xl/6 lg:text-3xl font-black lg:w-60 text-slate-700',
                              {
                                '!text-sm lg:!text-xl':
                                  item.subtitle.length >= 25,
                                '!text-xl': !item.description,
                              },
                            )}
                          >
                            {item.subtitle}
                          </h3>
                        </div>
                        <Icons
                          className={classNames(
                            item.color_icons,
                            'size-7 -rotate-12',
                          )}
                          fill="#FFFFFF"
                          type={item.icon_two}
                        />
                      </div>
                      {item.description && (
                        <p className="text-[10px] text-slate-700 font-bold lg:text-sm mt-1 lg:mt-2 text-center">
                          {item.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <span className="text-sm font-normal text-slate-700 px-5 lg:px-10 lg:text-base">
                  {item.description}
                </span>
              )}
              <span
                className={classNames(
                  'text-[8px] font-light lg:text-xs text-center mt-2 px-5 lg:px-10 w-full',
                  item.text_color_disclaimer,
                )}
              >
                {item.disclaimer}
              </span>
              {item.subtitle && item.disclaimer && (
                <a
                  href="/terms_and_cond_promotions"
                  className={classNames(
                    'text-[8px] font-light lg:text-xs text-center px-16 mb-3 lg:mb-5 underline',
                    item.text_color_disclaimer,
                  )}
                >
                  Ver términos y condiciones
                </a>
              )}
              {item.call_action && (
                <Button
                  onClick={() => window.open(item.url_action, '_blank')}
                  className={classNames(
                    '!text-xs lg:!text-base h-6 lg:h-10 !w-fit !rounded-full',
                    item.bg_button,
                  )}
                >
                  {item.call_action}
                </Button>
              )}
            </Card>
          </CarouselItem>
        ))}
      </CarouselContent>
    </div>
  </div>
);
